import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Mail, MapPin, Facebook } from "lucide-react";
import { NAV, SITE } from "../lib/site";
import { API } from "@/lib/auth";
import { track } from "@/lib/analytics";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), source: "footer" });
      setDone(true);
      toast.success(r.data?.already_subscribed ? "You're already on the list — thank you." : "You're in — welcome to the list.");
      if (!r.data?.already_subscribed) track("newsletter_signup", { source: "footer" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally { setBusy(false); }
  };
  if (done) {
    return (
      <p className="text-[hsl(var(--cream)/0.75)] text-sm">
        Thanks — check your inbox for the next drop. 💌
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="footer-newsletter">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        data-testid="newsletter-email"
        className="flex-1 h-11 rounded-full bg-[hsl(var(--cream)/0.08)] border border-[hsl(var(--cream)/0.15)] px-5 text-sm text-[hsl(var(--cream))] placeholder:text-[hsl(var(--cream)/0.4)] outline-none focus:border-[hsl(var(--cream)/0.4)]"
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="newsletter-submit"
        className="h-11 px-5 rounded-full bg-[hsl(var(--gold))] text-[hsl(var(--onyx))] text-sm font-medium disabled:opacity-60"
      >
        {busy ? "Signing up…" : "Subscribe"}
      </button>
    </form>
  );
};

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] mt-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-20">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="overline text-[hsl(var(--gold))]">Malek Dridi</div>
            <h3 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-balance">
              Luxury hair, extensions & education crafted with intent.
            </h3>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-book-cta"
              className="btn-pill btn-blush mt-8"
            >
              Book Appointment
            </a>
            <div className="mt-10">
              <div className="overline text-[hsl(var(--gold))]">Newsletter</div>
              <p className="mt-3 text-sm text-[hsl(var(--cream)/0.7)] max-w-sm">
                Exclusive salon offers, extension tips, and first dibs on new NøoksProRange drops. No spam, unsubscribe any time.
              </p>
              <NewsletterForm />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-[hsl(var(--gold))]">Explore</div>
            <ul className="mt-5 space-y-3 text-[hsl(var(--cream)/0.75)]">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.to.replace(/\//g, "") || "home"}`}
                    className="hover:text-[hsl(var(--cream))] transition-colors duration-300"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/blog" data-testid="footer-link-blog" className="hover:text-[hsl(var(--cream))] transition-colors duration-300">
                  Journal
                </Link>
              </li>
              <li>
                <Link to="/gift-vouchers" data-testid="footer-link-gift-vouchers" className="hover:text-[hsl(var(--cream))] transition-colors duration-300">
                  Gift Vouchers
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="overline text-[hsl(var(--gold))]">Contact</div>
            <ul className="mt-5 space-y-4 text-[hsl(var(--cream)/0.8)]">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-[hsl(var(--gold))]" />
                <span>{SITE.addressLine}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="mt-1 text-[hsl(var(--gold))]" />
                <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Instagram size={18} className="mt-1 text-[hsl(var(--gold))]" />
                <a href={SITE.instagramUrl} target="_blank" rel="noreferrer">
                  {SITE.instagramHandle}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <svg viewBox="0 0 24 24" width="18" height="18" className="mt-1 text-[hsl(var(--gold))] fill-current shrink-0" aria-hidden="true">
                  <path d="M19.6 6.7a5 5 0 0 1-3-1v9.5a5.7 5.7 0 1 1-5.7-5.7c.3 0 .6 0 .9.1v2.9a2.8 2.8 0 1 0 2 2.7V2h2.8a5 5 0 0 0 3 4.5v.2Z" />
                </svg>
                <a
                  href={SITE.tiktokUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-tiktok"
                >
                  {SITE.tiktokHandle} on TikTok
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Facebook size={18} className="mt-1 text-[hsl(var(--gold))] shrink-0" />
                <a
                  href={SITE.facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-facebook"
                >
                  {SITE.facebookHandle} on Facebook
                </a>
              </li>
            </ul>

            <div className="overline text-[hsl(var(--gold))] mt-10">Opening Hours</div>
            <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--cream)/0.75)]">
              {SITE.hours.map((h) => (
                <li key={h.day} className="flex justify-between">
                  <span>{h.day}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 space-y-3 text-xs text-[hsl(var(--cream)/0.5)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span>© {new Date().getFullYear()} Malek Dridi Hair & Beauty. All rights reserved.</span>
            <div className="flex items-center gap-4 flex-wrap">
              <Link to="/legal/privacy" data-testid="footer-link-privacy" className="hover:text-[hsl(var(--cream))]">Privacy</Link>
              <Link to="/legal/terms" data-testid="footer-link-terms" className="hover:text-[hsl(var(--cream))]">Terms</Link>
              <Link to="/legal/returns" data-testid="footer-link-returns" className="hover:text-[hsl(var(--cream))]">Returns</Link>
              <Link to="/legal/course-terms" className="hover:text-[hsl(var(--cream))]">Course terms</Link>
              <Link to="/legal/trade-terms" className="hover:text-[hsl(var(--cream))]">Trade terms</Link>
            </div>
          </div>
          <p className="text-[hsl(var(--cream)/0.4)] leading-relaxed">
            Malek Dridi trading as Malek Dridi Hair &amp; Beauty · Sole trader · 58 Love Street, Paisley, PA3 2DY, Scotland, United Kingdom.
          </p>
        </div>
      </div>
    </footer>
  );
};
